import { create } from 'zustand';

const useManagementStore = create((set, get) => ({
  // State
  expedientsList: [],
  whatsappInteractionsList: [],
  currentPage: 1,
  totalPages: 1,
  loading: false,

  // Actions
  setExpedientsList: expedients =>
    set({
      expedientsList: expedients,
    }),

  setPagination: (currentPage, totalPages) => set({ currentPage, totalPages }),

  setLoading: loading => set({ loading }),

  // Update interaction status (for ACK updates)
  updateInteractionStatus: (expedientId, msgId, status) => {
    set(state => ({
      expedientsList: state.expedientsList.map(expedient => {
        if (expedient._id === expedientId) {
          //console.log('Updating interaction status:', expedient.interactions);
          return {
            ...expedient,
            interactions: expedient.interactions.map(interaction => {
              if (interaction.details?.msgId === msgId) {
                //console.log('Updating interaction status:', interaction.details?.msgId, status);
                return { ...interaction, status };
              }
              return interaction;
            }),
          };
        }
        return expedient;
      }),
    }));
  },

  // Insert expedient at the top of the list, or replace if it already exists.
  // Used by socket handlers to merge a single fetched expedient without
  // reloading the whole page.
  upsertExpedient: expedient => {
    if (!expedient) return;
    const targetId = expedient._id?.toString() || expedient.expedient_id?.toString();
    if (!targetId) return;
    set(state => {
      const existingIndex = state.expedientsList.findIndex(
        exp => (exp._id?.toString() || exp.expedient_id?.toString()) === targetId
      );
      if (existingIndex === -1) {
        return { expedientsList: [expedient, ...state.expedientsList] };
      }
      const next = [...state.expedientsList];
      next[existingIndex] = expedient;
      return { expedientsList: next };
    });
  },

  // Add new interaction
  addInteraction: (expedientId, newInteraction) => {
    set(state => {
      const expedients = state.expedientsList.map(expedient => {
        if (expedient._id === expedientId) {
          return {
            ...expedient,
            interactions: [...expedient.interactions, newInteraction],
            total_interactions: expedient.total_interactions + 1,
          };
        }
        return expedient;
      });

      return {
        expedientsList: expedients,
      };
    });
  },

  // Remove interaction
  removeInteraction: (expedientId, interactionId) => {
    set(state => ({
      expedientsList: state.expedientsList.map(expedient => {
        if (expedient._id === expedientId) {
          return {
            ...expedient,
            interactions: expedient.interactions.filter(
              interaction => (interaction.interaction_id || interaction._id) !== interactionId
            ),
            total_interactions: Math.max(0, expedient.total_interactions - 1),
          };
        }
        return expedient;
      }),
    }));
  },

  // Update interaction (for status changes)
  updateInteraction: (expedientId, interactionId, updates) => {
    set(state => ({
      expedientsList: state.expedientsList.map(expedient => {
        if (expedient._id === expedientId) {
          return {
            ...expedient,
            interactions: expedient.interactions.map(interaction =>
              interaction.interaction_id === interactionId ? { ...interaction, ...updates } : interaction
            ),
          };
        }
        return expedient;
      }),
    }));
  },

  // Get interactions for a specific expedient
  getExpedientInteractions: expedientId => {
    const state = get();
    const expedient = state.expedientsList.find(exp => exp._id === expedientId);
    return expedient ? expedient.interactions : [];
  },

  // Get WhatsApp interactions for a specific expedient
  getWhatsAppInteractions: expedientId => {
    const state = get();
    const expedient = state.expedientsList.find(exp => exp._id === expedientId);
    if (!expedient) return [];

    return expedient.interactions.filter(
      interaction => interaction.type === 'whatsapp_sent' || interaction.type === 'whatsapp_response'
    );
  },

  // Reset store
  reset: () =>
    set({
      expedientsList: [],
      currentPage: 1,
      totalPages: 1,
      loading: false,
    }),
}));

export default useManagementStore;
